import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowRight, Bell, MessagesSquare, MoreHorizontal, Trash2 } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiError, stream } from '@/api';
import { useAuth } from '@/auth';
import { Avatar, MessageView, Streaming } from '@/chat/Bubbles';
import { Composer, type OutFile } from '@/chat/Composer';
import {
  type Action,
  markRead,
  type Message,
  putAction,
  putMessage,
  threadsKey,
  useActions,
  useLiveThread,
  unread,
  useMessages,
  useThreads,
} from '@/chat/data';
import { RoundButton, TopBar } from '@/chat/TopBar';
import { supabase, unwrap } from '@/supabase';

/**
 * The first message of a new chat. The conversation is created only when something is sent,
 * so opening the app doesn't leave empty chats behind; the message then moves with the user
 * from / to the new conversation's page, which sends it.
 */
let firstMessage: { threadId: string; text: string; files: OutFile[] } | null = null;

/** Opening FarmAI lands here: an empty conversation, ready for a question or a voice note. */
export function NewChatPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const anyUnread = useThreads().data?.some(unread);
  const start = useMutation({
    mutationFn: async (_first: { text: string; files: OutFile[] }) =>
      unwrap(await supabase.from('chat_threads').insert({}).select('id').single()),
    onSuccess: ({ id }, first) => {
      firstMessage = { threadId: id, ...first };
      void qc.invalidateQueries({ queryKey: threadsKey });
      void navigate({ to: '/t/$threadId', params: { threadId: id }, replace: true });
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <div className="flex h-full flex-col">
      <TopBar
        title="FarmAI"
        start={
          <RoundButton asChild className="relative md:invisible">
            <Link to="/chats" aria-label={t('threads.list')}>
              <MessagesSquare />
              {anyUnread && (
                <span className="absolute end-1.5 top-1.5 size-2.5 rounded-full bg-brand ring-2 ring-panel" />
              )}
            </Link>
          </RoundButton>
        }
      />
      <div className="grid flex-1 place-items-center p-8 text-center">
        <div className="flex max-w-sm flex-col items-center gap-3">
          <Avatar className="size-16 shadow-pop" />
          <p className="text-2xl font-semibold tracking-tight" dir="auto">
            {t('chat.hello', { name: me?.name ?? '' })}
          </p>
          <p className="text-muted">{me?.enabled ? t('chat.start_hint') : t('chat.disabled')}</p>
        </div>
      </div>
      <Composer
        disabled={!me?.enabled || start.isPending}
        onSend={(text, files) => start.mutate({ text, files })}
      />
    </div>
  );
}

export function ChatPage() {
  const { threadId } = useParams({ from: '/app/t/$threadId' });
  const { t } = useTranslation();
  const { me, session } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const thread = useThreads().data?.find((x) => x.id === threadId);
  const messages = useMessages(threadId);
  const actions = useActions(threadId);
  useLiveThread(threadId);
  const [streaming, setStreaming] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);

  const count = messages.data?.length ?? 0;
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
    if (count) void markRead(threadId).then(() => qc.invalidateQueries({ queryKey: threadsKey }));
  }, [count, streaming, threadId, qc]);

  const send = useMutation({
    mutationFn: async ({ text, files }: { text: string; files: OutFile[] }) => {
      const uid = session!.user.id;
      const attachments = [];
      for (const f of files) {
        const path = `${uid}/${threadId}/${crypto.randomUUID()}.${f.ext}`;
        unwrap(
          await supabase.storage.from('chat-media').upload(path, f.body, { contentType: f.mime }),
        );
        attachments.push({
          path,
          mime: f.mime,
          bytes: f.body.size,
          ...(f.duration_ms && { duration_ms: Math.round(f.duration_ms) }),
        });
      }
      setStreaming('');
      await stream(`/threads/${threadId}/messages`, { text, attachments }, (event, data) => {
        if (event === 'message') putMessage(qc, data as Message);
        else if (event === 'delta')
          setStreaming((s) => (s ?? '') + (data as { text: string }).text);
        else if (event === 'action') {
          const a = data as { action: Action; message?: Message };
          putAction(qc, a.action);
          if (a.message) putMessage(qc, a.message);
        } else if (event === 'error')
          toast.error(
            (data as { error: string }).error === 'unavailable'
              ? t('chat.unavailable')
              : t('chat.failed'),
          );
      });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError && e.status === 429
          ? t('chat.too_many')
          : e instanceof ApiError && e.status === 403
            ? t('chat.disabled')
            : t('common.error'),
      ),
    onSettled: () => {
      setStreaming(null);
      void qc.invalidateQueries({ queryKey: threadsKey });
    },
  });

  const { mutate } = send;
  useEffect(() => {
    if (firstMessage?.threadId !== threadId) return;
    const { text, files } = firstMessage;
    firstMessage = null;
    mutate({ text, files });
  }, [threadId, mutate]);

  const remove = useMutation({
    mutationFn: async () => unwrap(await supabase.from('chat_threads').delete().eq('id', threadId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: threadsKey });
      void navigate({ to: '/chats' });
    },
  });

  const title =
    thread?.kind === 'inbox' ? t('threads.inbox') : thread?.title || t('threads.untitled');
  return (
    <div className="flex h-full flex-col">
      <TopBar
        title={
          thread?.kind === 'inbox' ? (
            <span className="inline-flex items-center gap-1.5">
              <Bell className="size-4 text-brand" aria-hidden /> {title}
            </span>
          ) : (
            title
          )
        }
        start={
          <RoundButton asChild className="md:invisible">
            <Link to="/chats" aria-label={t('common.back')}>
              <ArrowRight className="ltr:-scale-x-100" />
            </Link>
          </RoundButton>
        }
        end={
          thread?.kind === 'chat' && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <RoundButton aria-label={t('chat.more')}>
                  <MoreHorizontal />
                </RoundButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className="z-50 min-w-44 animate-pop rounded-[18px] bg-panel p-1.5 shadow-pop ring-1 ring-line/60"
                >
                  <DropdownMenu.Item
                    disabled={remove.isPending}
                    onSelect={() => remove.mutate()}
                    className="flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-gap outline-none data-[highlighted]:bg-gap/10"
                  >
                    <Trash2 className="size-4" aria-hidden /> {t('threads.delete')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )
        }
      />
      <div className="flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4" role="log">
        {messages.data?.map((m) => (
          <MessageView
            key={m.id}
            m={m}
            action={actions.data?.get((m.payload as { action_id?: string }).action_id ?? '')}
          />
        ))}
        {streaming != null && <Streaming text={streaming} />}
        <div ref={end} />
      </div>
      <Composer
        disabled={!me?.enabled || send.isPending}
        onSend={(text, files) => send.mutate({ text, files })}
      />
    </div>
  );
}
